// @ts-check

/** @file Renders a user's rounded profile picture with an initials fallback. */

import {memo, useEffect, useMemo, useState} from "react"
import {Image, View} from "react-native"
import Text from "@kaspernj/api-maker/build/utils/text"

import {loadUserProfilePictureSource} from "../profile-pictures/profile-picture-source"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * Renders one user avatar and loads its optional attachment when no source is provided.
 * @param {{displayName: string, size?: number, source?: string | null, testID: string, userId: string | number}} props Component props.
 * @returns {ReactNode} Rounded image or initials fallback.
 * @throws {Error} When attachment loading fails unexpectedly.
 */
function UserAvatar({displayName, size = 28, source, testID, userId}) {
  const colors = useThemeColors()
  const themeName = useThemeName()
  const [loadError, setLoadError] = useState(/** @type {Error | null} */ (null))
  const [loadedSource, setLoadedSource] = useState(/** @type {string | null} */ (null))

  useEffect(() => {
    if (source !== undefined) return undefined

    let cancelled = false
    void loadUserProfilePictureSource(userId).then((nextSource) => {
      if (!cancelled) setLoadedSource(nextSource)
    }).catch((error) => {
      if (!cancelled) setLoadError(error instanceof Error ? error : new Error(String(error)))
    })

    return () => {
      cancelled = true
    }
  }, [source, userId])

  const resolvedSource = source === undefined ? loadedSource : source
  const imageSource = useMemo(() => resolvedSource ? {uri: resolvedSource} : null, [resolvedSource])
  if (loadError) throw loadError

  if (imageSource) {
    return (
      <Image
        accessibilityLabel={displayName}
        source={imageSource}
        style={styles[`image-${size}`] ||= {
          borderRadius: size / 2,
          height: size,
          width: size
        }}
        testID={`${testID}/image`}
      />
    )
  }

  return (
    <View
      accessibilityLabel={displayName}
      style={styles[`fallback-${themeName}-${size}`] ||= {
        alignItems: "center",
        backgroundColor: colors.chipBackground,
        borderRadius: size / 2,
        height: size,
        justifyContent: "center",
        width: size
      }}
      testID={`${testID}/fallback`}
    >
      <Text
        style={styles[`initials-${themeName}-${size}`] ||= {
          color: colors.chipText,
          fontSize: Math.max(10, Math.round(size * 0.36)),
          fontWeight: 700
        }}
        testID={`${testID}/initials`}
      >
        {initialsForDisplayName(displayName)}
      </Text>
    </View>
  )
}

/**
 * Builds at most two display initials.
 * @param {string} displayName User-facing name.
 * @returns {string} Uppercase initials.
 */
function initialsForDisplayName(displayName) {
  const nameParts = displayName.trim().split(/\s+/).filter(Boolean)
  if (nameParts.length === 0) return "?"

  const selectedParts = nameParts.length === 1
    ? nameParts
    : [nameParts[0], nameParts[nameParts.length - 1]]
  return selectedParts.map((part) => part.slice(0, 1).toUpperCase()).join("")
}

export default memo(UserAvatar)
